import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { supabase } from '../supabaseClient';
import { 
  Moon, Sun, Bell, BellOff, Globe, Mail, 
  Clock, MessageSquare, Eye, Save, User
} from 'lucide-react';

const UserSettings = () => {
  const { user } = useAuth0();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  
  // Benutzereinstellungen
  const [settings, setSettings] = useState({
    // Erscheinungsbild
    theme: 'dark', // light, dark, system
    fontSize: 'medium', // small, medium, large
    
    // Benachrichtigungen
    emailNotifications: true,
    pushNotifications: true,
    newsletterEnabled: false,
    
    // Sprache & Region
    language: 'de',
    timeFormat: '24h', // 12h, 24h
    
    // Privatsphäre & Sicherheit
    dataCollection: true,
    activityTracking: true,
    
    // Kommunikation
    autoReply: false,
    messagePreview: true
  });

  // Optionen für Auswahlfelder
  const themeOptions = [
    { value: 'light', label: 'Hell' },
    { value: 'dark', label: 'Dunkel' },
    { value: 'system', label: 'Systemeinstellung' }
  ];
  
  const fontSizeOptions = [
    { value: 'small', label: 'Klein' },
    { value: 'medium', label: 'Mittel' },
    { value: 'large', label: 'Groß' }
  ];
  
  const languageOptions = [
    { value: 'de', label: 'Deutsch' },
    { value: 'en', label: 'Englisch' },
    { value: 'fr', label: 'Französisch' },
    { value: 'es', label: 'Spanisch' }
  ];
  
  const timeFormatOptions = [
    { value: '12h', label: '12-Stunden (AM/PM)' },
    { value: '24h', label: '24-Stunden' }
  ];

  // Einstellungen laden
  useEffect(() => {
    // Laden aus localStorage
    const savedSettings = localStorage.getItem('userSettings');
    if (savedSettings) {
      try {
        setSettings(JSON.parse(savedSettings));
      } catch (e) {
        console.error('Fehler beim Parsen der gespeicherten Einstellungen');
      }
    }
    setLoading(false);
  }, []);
  
  // Speicherfunktion anpassen
  const saveSettings = () => {
    try {
      setSaving(true);
      localStorage.setItem('userSettings', JSON.stringify(settings));
      setSuccessMessage('Einstellungen erfolgreich gespeichert');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Fehler beim Speichern der Einstellungen:', error);
    } finally {
      setSaving(false);
    }
  };
  // Handler für Einstellungsänderungen
  const handleChange = (setting, value) => {
    setSettings(prev => ({
      ...prev,
      [setting]: value
    }));
  };

  // Theme-Umschaltung im gesamten System anwenden
  useEffect(() => {
    // Theme anwenden
    if (settings.theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else if (settings.theme === 'light') {
      document.documentElement.classList.remove('dark');
    } else if (settings.theme === 'system') {
      // System-Präferenzen überprüfen
      if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
    }
    
    // Schriftgröße anwenden
    document.documentElement.dataset.fontSize = settings.fontSize;
    
  }, [settings.theme, settings.fontSize]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 dark:bg-gray-900 min-h-screen">
      <div className="max-w-4xl mx-auto py-10 px-4 sm:px-6">
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Einstellungen</h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Passen Sie die Anwendung an Ihre Bedürfnisse an
            </p>
          </div>
          
          <button
            onClick={saveSettings}
            disabled={saving}
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (
              <>
                <div className="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                Speichern...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Einstellungen speichern
              </>
            )}
          </button>
        </div>
        
        {successMessage && (
          <div className="mb-6 p-3 bg-green-100 text-green-700 rounded-md">
            {successMessage}
          </div>
        )}

        <div className="bg-white dark:bg-gray-800 shadow rounded-lg divide-y divide-gray-200 dark:divide-gray-700">
          {/* Erscheinungsbild */}
          <div className="p-6">
            <div className="flex items-center">
              {settings.theme === 'dark' ? (
                <Moon className="h-6 w-6 text-gray-400 dark:text-gray-300" />
              ) : (
                <Sun className="h-6 w-6 text-gray-400 dark:text-gray-300" />
              )}
              <h2 className="ml-3 text-lg font-medium text-gray-900 dark:text-white">Erscheinungsbild</h2>
            </div>
            
            <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">
              <div>
                <label htmlFor="theme" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Farbschema
                </label>
                <select
                  id="theme"
                  name="theme"
                  value={settings.theme}
                  onChange={(e) => handleChange('theme', e.target.value)}
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  {themeOptions.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label htmlFor="fontSize" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Schriftgröße
                </label>
                <select
                  id="fontSize"
                  name="fontSize"
                  value={settings.fontSize}
                  onChange={(e) => handleChange('fontSize', e.target.value)}
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  {fontSizeOptions.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Benachrichtigungen */}
          <div className="p-6">
            <div className="flex items-center">
              <Bell className="h-6 w-6 text-gray-400 dark:text-gray-300" />
              <h2 className="ml-3 text-lg font-medium text-gray-900 dark:text-white">Benachrichtigungen</h2>
            </div>
            
            <div className="mt-6 space-y-4">
              <div className="flex items-start">
                <div className="flex items-center h-5">
                  <input
                    id="emailNotifications"
                    name="emailNotifications"
                    type="checkbox"
                    checked={settings.emailNotifications}
                    onChange={(e) => handleChange('emailNotifications', e.target.checked)}
                    className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded dark:bg-gray-700 dark:border-gray-600"
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label htmlFor="emailNotifications" className="font-medium text-gray-700 dark:text-gray-300">E-Mail-Benachrichtigungen</label>
                  <p className="text-gray-500 dark:text-gray-400">Erhalten Sie wichtige Aktualisierungen per E-Mail.</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="flex items-center h-5">
                  <input
                    id="pushNotifications"
                    name="pushNotifications"
                    type="checkbox"
                    checked={settings.pushNotifications}
                    onChange={(e) => handleChange('pushNotifications', e.target.checked)}
                    className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded dark:bg-gray-700 dark:border-gray-600"
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label htmlFor="pushNotifications" className="font-medium text-gray-700 dark:text-gray-300">Push-Benachrichtigungen</label>
                  <p className="text-gray-500 dark:text-gray-400">Erhalten Sie Echtzeit-Benachrichtigungen im Browser.</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="flex items-center h-5">
                  <input
                    id="newsletterEnabled"
                    name="newsletterEnabled"
                    type="checkbox"
                    checked={settings.newsletterEnabled}
                    onChange={(e) => handleChange('newsletterEnabled', e.target.checked)}
                    className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded dark:bg-gray-700 dark:border-gray-600"
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label htmlFor="newsletterEnabled" className="font-medium text-gray-700 dark:text-gray-300">Newsletter</label>
                  <p className="text-gray-500 dark:text-gray-400">Erhalten Sie monatliche Updates zu neuen Funktionen.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Sprache & Region */}
          <div className="p-6">
            <div className="flex items-center">
              <Globe className="h-6 w-6 text-gray-400 dark:text-gray-300" />
              <h2 className="ml-3 text-lg font-medium text-gray-900 dark:text-white">Sprache & Region</h2>
            </div>
            
            <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">
              <div>
                <label htmlFor="language" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Sprache
                </label>
                <select
                  id="language"
                  name="language"
                  value={settings.language}
                  onChange={(e) => handleChange('language', e.target.value)}
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  {languageOptions.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label htmlFor="timeFormat" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Zeitformat
                </label>
                <select
                  id="timeFormat"
                  name="timeFormat"
                  value={settings.timeFormat}
                  onChange={(e) => handleChange('timeFormat', e.target.value)}
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  {timeFormatOptions.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Privatsphäre & Sicherheit */}
          <div className="p-6">
            <div className="flex items-center">
              <Eye className="h-6 w-6 text-gray-400 dark:text-gray-300" />
              <h2 className="ml-3 text-lg font-medium text-gray-900 dark:text-white">Privatsphäre & Sicherheit</h2>
            </div>
            
            <div className="mt-6 space-y-4">
              <div className="flex items-start">
                <div className="flex items-center h-5">
                  <input
                    id="dataCollection"
                    name="dataCollection"
                    type="checkbox"
                    checked={settings.dataCollection}
                    onChange={(e) => handleChange('dataCollection', e.target.checked)}
                    className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded dark:bg-gray-700 dark:border-gray-600"
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label htmlFor="dataCollection" className="font-medium text-gray-700 dark:text-gray-300">Nutzungsdaten teilen</label>
                  <p className="text-gray-500 dark:text-gray-400">Hilf uns, die Anwendung zu verbessern, indem du anonyme Nutzungsdaten teilst.</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="flex items-center h-5">
                  <input
                    id="activityTracking"
                    name="activityTracking"
                    type="checkbox"
                    checked={settings.activityTracking}
                    onChange={(e) => handleChange('activityTracking', e.target.checked)}
                    className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded dark:bg-gray-700 dark:border-gray-600"
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label htmlFor="activityTracking" className="font-medium text-gray-700 dark:text-gray-300">Aktivitätsverfolgung</label>
                  <p className="text-gray-500 dark:text-gray-400">Zeige deine Aktivitäten anderen Nutzern an.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Kommunikation */}
          <div className="p-6">
            <div className="flex items-center">
              <MessageSquare className="h-6 w-6 text-gray-400 dark:text-gray-300" />
              <h2 className="ml-3 text-lg font-medium text-gray-900 dark:text-white">Kommunikation</h2>
            </div>
            
            <div className="mt-6 space-y-4">
              <div className="flex items-start">
                <div className="flex items-center h-5">
                  <input
                    id="autoReply"
                    name="autoReply"
                    type="checkbox"
                    checked={settings.autoReply}
                    onChange={(e) => handleChange('autoReply', e.target.checked)}
                    className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded dark:bg-gray-700 dark:border-gray-600"
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label htmlFor="autoReply" className="font-medium text-gray-700 dark:text-gray-300">Automatische Antworten</label>
                  <p className="text-gray-500 dark:text-gray-400">Aktiviere automatische Antworten, wenn du nicht verfügbar bist.</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="flex items-center h-5">
                  <input
                    id="messagePreview"
                    name="messagePreview"
                    type="checkbox"
                    checked={settings.messagePreview}
                    onChange={(e) => handleChange('messagePreview', e.target.checked)}
                    className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded dark:bg-gray-700 dark:border-gray-600"
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label htmlFor="messagePreview" className="font-medium text-gray-700 dark:text-gray-300">Nachrichtenvorschau</label>
                  <p className="text-gray-500 dark:text-gray-400">Zeige Vorschau von Nachrichten in Benachrichtigungen an.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserSettings;